// Select.tsx
import React, { useState, useRef, useEffect } from 'react';
import styles from './Select.module.css';
import { Spinner } from "@/shared/components/common/Spinner/Spinner";
import { DropdownPortal } from "./DropdownPortal";

type Option = {
  value: string;
  label: string;
};

type SelectProps = {
  label?: string;
  options: Option[];
  value?: string;
  onChange?: (value: string, name?: string) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  loading?: boolean;
  loadingText?: string; // Новый пропс для текста при загрузке
  errorText?: string;   // Новый пропс для текста при ошибке
  required?: boolean;   // Новый пропс required
  name?: string;        // Новый пропс name
};

export const Select: React.FC<SelectProps> = ({
  label,
  options,
  value = '',
  onChange,
  placeholder = 'Выберите...',
  error,
  disabled,
  className,
  loading,
  loadingText = 'Загрузка...', // Значение по умолчанию
  errorText = 'Ошибка',         // Значение по умолчанию
  required = false,             // Значение по умолчанию
  name,                         // Новый пропс name
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selectBoxRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        ref.current && !ref.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const selected = options.find(opt => opt.value === value);
  const isDisabled = disabled || loading || !!error;

  return (
    <div className={styles.selectWrapper + (className ? ' ' + className : '')} ref={ref}>
      {label && (
        <label className={styles.label}>
          {label}
          {required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
        </label>
      )}
      <div
        ref={selectBoxRef}
        className={[
          styles.selectBox,
          open && !isDisabled ? styles.open : '',
          isDisabled ? styles.disabled : '',
          error ? styles.errorBox : '',
          required && !value ? styles.required : '',
        ].join(' ')}
        tabIndex={isDisabled ? -1 : 0}
        onClick={() => !isDisabled && setOpen(o => !o)}
        onKeyDown={e => {
          if (isDisabled) return;
          if (e.key === 'Enter' || e.key === ' ') setOpen(o => !o);
          if (e.key === 'Escape') setOpen(false);
        }}
        aria-disabled={isDisabled}
        aria-required={required}
        data-name={name}
      >
        <span className={selected ? styles.value : styles.placeholder}>
          {loading ? (
            <span className={styles.loadingContainer}>
              {loadingText}
              <Spinner size="small" className={styles.spinner} />
            </span>
          ) : error ? (
            errorText
          ) : selected ? (
            selected.label
          ) : (
            placeholder
          )}
        </span>
        {!loading && <span className={styles.arrow} />}
      </div>
      {open && !isDisabled && (
        <DropdownPortal
          anchorRef={selectBoxRef}
          open={open}
          className={styles.dropdown}
          ref={dropdownRef}
        >
          {options.map(opt => (
            <li
              key={opt.value}
              className={[
                styles.option,
                value === opt.value ? styles.selected : ''
              ].join(' ')}
              onClick={() => {
                if (required && opt.value === '') return; // Не даём выбрать пустое значение, если required
                if (opt.value !== value) onChange?.(opt.value, name);
                setOpen(false);
              }}
            >
              {opt.label}
            </li>
          ))}
        </DropdownPortal>
      )}
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
};